// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import * as Handlebars from "handlebars";
import { NewsBlockDTO, NewsInputDTO } from "./dto/news.dto";
import {
  TemplateDTO,
  TemplateFormatDto,
  TemplateInputImageDto,
  TemplateInputTextDto
} from "./dto/template.dto";
import { BadTemplateParameterError } from "./error/BadTemplateParameterError";
import sanitizeHtml from "sanitize-html";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

/*
 TODO Ce service et tout le contenu de utils/htmlTemplate est commun avec nbo-frontend.
 Pour l'instant, le code est dupliqué, il sera à mutualiser quand la solution propre sera trouvée.
 En attendant, il faut garder le code des 2 modules synchronisé.
 */
class TemplateHtmlService {
  constructor() {
    /* eslint-disable */
    Handlebars.registerHelper("eq", function() {
      const args = Array.prototype.slice.call(arguments, 0, -1);
      return args.every(function(expression) {
        return args[0] === expression;
      });
    });

    Handlebars.registerHelper("breaklines", function(text) {
      text = Handlebars.Utils.escapeExpression(text);
      text = text.replace(/(\r\n|\n|\r)/gm, "<br/>");
      return new Handlebars.SafeString(text);
    });
    /* eslint-enable */
  }

  private _generateNewsHeadJson(
    title: string,
    image: string,
    resume: string,
    eventStartDate?: string,
    eventEndDate?: string,
    color?: string
  ): NewsBlockDTO {
    const dateFormat = "EEEE dd MMMM yyyy à HH:mm";
    const block = new NewsBlockDTO();
    const input1 = new NewsInputDTO();
    input1.inputType = "text";
    input1.content = title;
    const input2 = new NewsInputDTO();
    input2.inputType = "img";
    input2.content = image;
    const input3 = new NewsInputDTO();
    input3.inputType = "text";
    input3.content = color || "";
    const input4 = new NewsInputDTO();
    input4.inputType = eventStartDate ? "text" : "";
    input4.content = eventStartDate
      ? format(new Date(eventStartDate), dateFormat, { locale: fr })
      : "";
    const input5 = new NewsInputDTO();
    input5.inputType = eventEndDate ? "text" : "";
    input5.content = eventEndDate
      ? format(new Date(eventEndDate), dateFormat, { locale: fr })
      : "";
    const input6 = new NewsInputDTO();
    input6.inputType = "text";
    input6.content = resume;
    block.inputs = [input1, input2, input3, input4, input5, input6];

    return JSON.parse(JSON.stringify(block)) as NewsBlockDTO;
  }

  public generateContentHtmlComplete(
    blocks: NewsBlockDTO[],
    title: string,
    image: string,
    resume: string,
    newsHeadTemplate: string,
    templates: TemplateDTO[],
    eventStartDate?: string,
    eventEndDate?: string,
    color?: string
  ): string {
    let template;
    let result: string;
    result = "";

    const headJson = this._generateNewsHeadJson(
      title,
      image,
      resume,
      eventStartDate,
      eventEndDate,
      color
    );
    /* eslint-disable */
    template = Handlebars.compile(newsHeadTemplate);
    result = result.concat(template(headJson));
    /* eslint-enable */

    const size = blocks.length;
    if (!size || size === 0) {
      throw new Error("The json of content of news should be an array");
    }

    for (let i = 0; i < size; i++) {
      const block = blocks[i];
      const templateEntity = this._findTemplateById(
        block.templateId,
        templates
      );
      if (!block.templateId) {
        throw new Error("Json format invalid");
      }
      if (templateEntity === null) {
        throw new Error("Template not exist, please check typeBlock");
      }
      this.verifyContentFormat(block, templateEntity.format);
      /* eslint-disable */
      const html = templateEntity.templateHtml;
      template = Handlebars.compile(html);
      result = result.concat(template(block));
      /* eslint-enable */
    }

    const defaultSpanAttributes =
      sanitizeHtml.defaults.allowedAttributes.span || [];
    const defaultDivAttributes =
      sanitizeHtml.defaults.allowedAttributes.div || [];
    const defaultImgAttributes =
      sanitizeHtml.defaults.allowedAttributes.img || [];
    const sanitizeOptions: sanitizeHtml.IOptions = {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat([
        "img",
        "del",
        "iframe"
      ]),
      allowedAttributes: {
        ...sanitizeHtml.defaults.allowedAttributes,
        span: defaultSpanAttributes.concat(["class"]),
        div: defaultDivAttributes.concat(["class", "style"]),
        img: defaultImgAttributes.concat(["alt"]),
        strong: defaultDivAttributes.concat(["class"]),
        iframe: ["src", "class"]
      },
      allowedSchemesByTag: {
        ...sanitizeHtml.defaults.allowedSchemesByTag,
        img: ["data", "http", "https"]
      }
    };
    const sanitizedResult = sanitizeHtml(result, sanitizeOptions);
    return sanitizedResult;
  }

  public verifyContentFormat(
    block: NewsBlockDTO,
    format: TemplateFormatDto
  ): boolean {
    const contentSize = block.inputs.length;
    const formatSize = format.inputs.length;
    if (contentSize !== formatSize) return false;
    for (let i = 0; i < contentSize; i++) {
      if (format.inputs[i].inputType !== block.inputs[i].inputType) {
        throw new BadTemplateParameterError(
          "Le contenu de la news ne correspond pas au modèle"
        );
      }
      if (block.inputs[i].inputType === "text") {
        const formatTextInput = format?.inputs?.[i] as TemplateInputTextDto;
        if (
          block.inputs[i].content === null ||
          block.inputs[i].content === undefined ||
          block.inputs[i].content.length === 0
        ) {
          throw new BadTemplateParameterError(
            "Un bloc de texte est vide, veuillez saisir tous les blocs de texte"
          );
        }
        const maxSize = formatTextInput?.properties?.maxSize;
        if (maxSize && block.inputs[i].content.length > maxSize) {
          throw new BadTemplateParameterError(
            `Le texte dans le bloc 
            ${i}
              est trop long. La longueur maximale pour ce bloc est 
              ${formatTextInput.properties.maxSize}`
          );
        }
      } else if (block.inputs[i].inputType === "img") {
        const formatImageInput = format?.inputs?.[i] as TemplateInputImageDto;
        if (
          block.inputs[i].content === null ||
          block.inputs[i].content === undefined ||
          block.inputs[i].content.length === 0
        ) {
          throw new BadTemplateParameterError(
            "Un bloc image est vide, veuillez sélectionner une image"
          );
        }
        const maxWeight = formatImageInput?.properties?.maxWeight;
        if (maxWeight && block.inputs[i].content.length > maxWeight) {
          throw new BadTemplateParameterError(
            `L'image du bloc 
              ${i}
               est trop volumineuse. La taille maximale pour ce bloc est 
              ${formatImageInput.properties.maxWeight}`
          );
        }
      }
    }
    return true;
  }

  private _findTemplateById(id: string, templates: TemplateDTO[]): TemplateDTO {
    return templates.filter(template => template.id === id)[0];
  }
}

const service = new TemplateHtmlService();

export default service;
